// Password Validation Utilities

import {
  PasswordStrength,
  PasswordStrengthResult,
  PasswordRequirements,
  DEFAULT_PASSWORD_REQUIREMENTS
} from '../types/registration';

/**
 * List of common/weak passwords to prevent
 * This is a subset - in production, use a more comprehensive list
 */
const COMMON_PASSWORDS = [
  'password', 'password123', '12345678', 'qwerty', 'abc123',
  'monkey', '1234567890', 'letmein', 'trustno1', 'dragon',
  'baseball', 'iloveyou', 'master', 'sunshine', 'ashley',
  'bailey', 'passw0rd', 'shadow', '123123', '654321',
  'superman', 'qazwsx', 'michael', 'football', 'welcome',
  'jesus', 'ninja', 'mustang', 'password1', '123456789',
  'admin', 'admin123', 'root', 'toor', 'pass', 'test',
  'guest', 'oracle', 'changeme', 'welcome123', 'user'
];

/**
 * Check if password is in the common passwords list
 */
export const isCommonPassword = (password: string): boolean => {
  const lowercasePassword = password.toLowerCase();
  return COMMON_PASSWORDS.some(common => 
    lowercasePassword === common || 
    lowercasePassword.includes(common)
  );
};

/**
 * Cached special character regex to avoid repeated compilation
 */
const createSpecialCharsRegex = (specialChars: string): RegExp => {
  return new RegExp(`[${specialChars.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}]`);
};

/**
 * Validate password against requirements
 */
export const validatePassword = (
  password: string,
  requirements: PasswordRequirements = DEFAULT_PASSWORD_REQUIREMENTS
): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Check minimum length
  if (password.length < requirements.minLength) {
    errors.push(`Password must be at least ${requirements.minLength} characters long`);
  }

  // Check for uppercase letters
  if (requirements.requireUppercase && !/[A-Z]/.test(password)) {
    errors.push('Password must contain at least one uppercase letter');
  }

  // Check for lowercase letters
  if (requirements.requireLowercase && !/[a-z]/.test(password)) {
    errors.push('Password must contain at least one lowercase letter');
  }

  // Check for numbers
  if (requirements.requireNumbers && !/\d/.test(password)) {
    errors.push('Password must contain at least one number');
  }

  // Check for special characters
  if (requirements.requireSpecialChars) {
    const specialCharsRegex = createSpecialCharsRegex(requirements.specialChars);
    if (!specialCharsRegex.test(password)) {
      errors.push('Password must contain at least one special character');
    }
  }

  // Check if password is too common
  if (isCommonPassword(password)) {
    errors.push('Password is too common. Please choose a stronger password');
  }

  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Analyze password strength
 */
export const analyzePasswordStrength = (
  password: string,
  requirements: PasswordRequirements = DEFAULT_PASSWORD_REQUIREMENTS
): PasswordStrengthResult => {
  let score = 0;
  const feedback: string[] = [];

  // Check individual requirements
  const hasUppercase = /[A-Z]/.test(password);
  const hasLowercase = /[a-z]/.test(password);
  const hasNumbers = /\d/.test(password);
  const hasSpecialChars = createSpecialCharsRegex(requirements.specialChars).test(password);
  const meetsMinLength = password.length >= requirements.minLength;

  const requirementsMet = {
    minLength: meetsMinLength,
    hasUppercase,
    hasLowercase,
    hasNumbers,
    hasSpecialChars
  };

  // Calculate base score from requirements
  if (meetsMinLength) score += 20;
  if (hasUppercase) score += 15;
  if (hasLowercase) score += 15;
  if (hasNumbers) score += 15;
  if (hasSpecialChars) score += 15;

  // Additional scoring factors
  // Length bonus
  if (password.length >= 12) score += 10;
  if (password.length >= 16) score += 10;

  // Variety bonus
  const uniqueChars = new Set(password).size;
  if (uniqueChars >= password.length * 0.6) score += 5;

  // Penalty for common passwords
  if (isCommonPassword(password)) {
    score = Math.max(0, score - 30);
    feedback.push('Avoid common passwords');
  }

  // Penalty for repeated characters
  const hasRepeatedChars = /(.)\1{2,}/.test(password);
  if (hasRepeatedChars) {
    score -= 10;
    feedback.push('Avoid repeated characters');
  }

  // Penalty for sequential characters (improved detection)
  const hasSequential = (() => {
    const lower = password.toLowerCase();
    // Check for sequential letters (forward and backward)
    for (let i = 0; i < lower.length - 2; i++) {
      const charCode = lower.charCodeAt(i);
      const nextCode = lower.charCodeAt(i + 1);
      const thirdCode = lower.charCodeAt(i + 2);
      
      // Forward sequences (abc, 123)
      if (nextCode === charCode + 1 && thirdCode === charCode + 2) {
        return true;
      }
      // Backward sequences (cba, 321)
      if (nextCode === charCode - 1 && thirdCode === charCode - 2) {
        return true;
      }
    }
    return false;
  })();
  
  if (hasSequential) {
    score -= 10;
    feedback.push('Avoid sequential characters');
  }

  // Cap score at 100
  score = Math.min(100, Math.max(0, score));

  // Determine strength level
  let strength: PasswordStrength;
  if (score < 20) {
    strength = PasswordStrength.WEAK;
    feedback.unshift('Password is very weak');
  } else if (score < 40) {
    strength = PasswordStrength.FAIR;
    feedback.unshift('Password is weak');
  } else if (score < 60) {
    strength = PasswordStrength.GOOD;
    feedback.unshift('Password is fair');
  } else if (score < 80) {
    strength = PasswordStrength.STRONG;
    feedback.unshift('Password is good');
  } else {
    strength = PasswordStrength.VERY_STRONG;
    feedback.unshift('Password is strong');
  }

  // Add helpful feedback
  if (!meetsMinLength) {
    feedback.push(`Use at least ${requirements.minLength} characters`);
  }
  if (!hasUppercase && requirements.requireUppercase) {
    feedback.push('Add uppercase letters');
  }
  if (!hasLowercase && requirements.requireLowercase) {
    feedback.push('Add lowercase letters');
  }
  if (!hasNumbers && requirements.requireNumbers) {
    feedback.push('Add numbers');
  }
  if (!hasSpecialChars && requirements.requireSpecialChars) {
    feedback.push('Add special characters');
  }

  const meetsRequirements = 
    (!requirements.requireUppercase || hasUppercase) &&
    (!requirements.requireLowercase || hasLowercase) &&
    (!requirements.requireNumbers || hasNumbers) &&
    (!requirements.requireSpecialChars || hasSpecialChars) &&
    meetsMinLength;

  return {
    strength,
    score,
    feedback,
    meetsRequirements,
    requirementsMet
  };
};

/**
 * Generate password strength color
 */
export const getPasswordStrengthColor = (strength: PasswordStrength): string => {
  switch (strength) {
    case PasswordStrength.WEAK:
      return '#f44336'; // red
    case PasswordStrength.FAIR:
      return '#ff9800'; // orange
    case PasswordStrength.GOOD:
      return '#ffc107'; // amber
    case PasswordStrength.STRONG:
      return '#4caf50'; // green
    case PasswordStrength.VERY_STRONG:
      return '#2e7d32'; // dark green
    default:
      return '#9e9e9e'; // grey
  }
};

/**
 * Get password strength label
 */
export const getPasswordStrengthLabel = (strength: PasswordStrength): string => {
  switch (strength) {
    case PasswordStrength.WEAK:
      return 'Weak';
    case PasswordStrength.FAIR:
      return 'Fair';
    case PasswordStrength.GOOD:
      return 'Good';
    case PasswordStrength.STRONG:
      return 'Strong';
    case PasswordStrength.VERY_STRONG:
      return 'Very Strong';
    default:
      return 'Unknown';
  }
};
